import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "react-router-dom";
import { accountApi } from "../../lib/account";
import { useAppBadge } from "../../hooks/useAppBadge";
import Button from "../ui/Button/Button";
import Header from "../ui/Header/Header";
import FeedbackModal from "../support/FeedbackModal";

type AuthenticatedLayoutProps = {
  children?: ReactNode;
};

const AuthenticatedLayout = ({ children }: AuthenticatedLayoutProps) => {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const navItems = [
    { href: "/dashboard", label: "Dashboard" },
    { href: "/gigs", label: "Opportunities" },
    { href: "/settings", label: "Settings" },
  ];
  const notificationsSummaryQuery = useQuery({
    queryKey: ["notifications-summary"],
    queryFn: () => accountApi.getNotificationSummary(),
    staleTime: 30_000,
    retry: 1,
  });
  const unreadCount = notificationsSummaryQuery.data?.unreadCount ?? 0;
  const badgeCount = unreadCount;
  useAppBadge(badgeCount, notificationsSummaryQuery.data !== undefined);

  return (
    <>
      <Header
        sticky
        navItems={navItems}
        mobileMenuBadgeCount={badgeCount}
        rightSlot={
          <div className="flex w-full items-center justify-between gap-2 md:w-auto md:justify-end">
            <Button variant="tertiary" size="sm" onClick={() => setIsFeedbackOpen(true)}>
              Feedback
            </Button>
            <Link
              to="/notifications"
              aria-label={badgeCount > 0 ? `${badgeCount} notifications` : "Notifications"}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-myteengig-border bg-white text-myteengig-text-muted transition-colors hover:border-myteengig-primary hover:text-myteengig-primary"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
                <path d="M10 21a2 2 0 0 0 4 0" />
              </svg>
              {badgeCount > 0 ? (
                <span
                  className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-myteengig-primary px-1 text-[11px] font-semibold text-white"
                >
                  {badgeCount > 99 ? "99+" : badgeCount}
                </span>
              ) : null}
            </Link>
          </div>
        }
      />
      {children ?? <Outlet />}
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </>
  );
};

export default AuthenticatedLayout;
